import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { Account, CustomerBaseData, UserDataModel } from '@zero/shared/util-models';
import { catchError, of, tap } from 'rxjs';
import { BaseState } from '../actions.base';
import { AlertStateFacade } from '../alert/alert-state-facade';
import { AuthStateFacade } from '../auth/auth-state.facade';
import { LoadContactData, LoadCustomerAccountData, LoadCustomerBaseData, ResetCustomerState } from './customer.actions';
import { CustomerInfrastructure } from './customer.infrastructure';

export interface CustomerStateModel {
  customerBaseData?: CustomerBaseData;
  customerBaseDataLoading: boolean;
  customerBaseDataInitiallyLoaded: boolean;
  accountData?: UserDataModel;
  accountDataLoading: boolean;
  accountDataInitiallyLoaded: boolean;
  customerAccountData?: Account;
  customerAccountDataLoading: boolean;
  customerAccountDataInitiallyLoaded: boolean;
}

const DEFAULT_STATE: CustomerStateModel = {
  customerBaseDataLoading: false,
  customerBaseDataInitiallyLoaded: false,
  accountDataLoading: false,
  accountDataInitiallyLoaded: false,
  customerAccountDataLoading: false,
  customerAccountDataInitiallyLoaded: false,
};

@State<CustomerStateModel>({
  name: 'customer',
  defaults: DEFAULT_STATE,
})
@Injectable()
export class CustomerStateModule extends BaseState {
  constructor(
    alertStateFacade: AlertStateFacade,
    actions$: Actions,
    private _customerInfrastructure: CustomerInfrastructure,
    private _authStateFacade: AuthStateFacade,
    store: Store,
  ) {
    super(alertStateFacade, actions$);
    this.userLoggedOut$.subscribe(() => store.dispatch(new ResetCustomerState()));
  }

  @Selector()
  public static customerBaseData(state: CustomerStateModel): CustomerBaseData {
    return state.customerBaseData;
  }

  @Selector()
  public static accountData(state: CustomerStateModel): UserDataModel {
    return state.accountData;
  }

  @Selector()
  public static accountDataLoading(state: CustomerStateModel): boolean {
    return state.accountDataLoading;
  }

  @Selector()
  public static customerAccountData(state: CustomerStateModel): Account {
    return state.customerAccountData;
  }

  @Action(ResetCustomerState)
  public resetCustomerState(ctx: StateContext<CustomerStateModel>) {
    return ctx.setState(DEFAULT_STATE);
  }

  @Action(LoadCustomerBaseData)
  public loadCustomerBaseData(ctx: StateContext<CustomerStateModel>, { payload }: LoadCustomerBaseData) {
    if (!this._authStateFacade.customerId) {
      return of();
    }

    const { force } = payload;

    if (!force && ctx.getState().customerBaseDataInitiallyLoaded) {
      return of();
    }

    ctx.patchState({ customerBaseDataLoading: true });

    return this._customerInfrastructure.getCustomerBaseData().pipe(
      tap((customerBaseData) =>
        ctx.patchState({
          customerBaseData,
          customerBaseDataLoading: false,
          customerBaseDataInitiallyLoaded: true,
        }),
      ),
      catchError((error: HttpErrorResponse) => {
        ctx.patchState({ customerBaseDataLoading: false });
        return this.handleBackendError(error);
      }),
    );
  }

  @Action(LoadContactData)
  public loadContactData(ctx: StateContext<CustomerStateModel>, { payload }: LoadContactData) {
    const { force } = payload;

    if (!force && ctx.getState().accountDataInitiallyLoaded) {
      return of();
    }

    ctx.patchState({ accountDataLoading: true });

    return this._customerInfrastructure.getAccountData().pipe(
      tap((accountData) => ctx.patchState({ accountData, accountDataLoading: false, accountDataInitiallyLoaded: true })),
      catchError((error: HttpErrorResponse) => {
        ctx.patchState({ accountDataLoading: false });
        return this.handleBackendError(error);
      }),
    );
  }

  @Action(LoadCustomerAccountData)
  public loadCustomerAccountData(ctx: StateContext<CustomerStateModel>, { payload }: LoadCustomerAccountData) {
    const { force } = payload;

    if (!force && ctx.getState().customerAccountDataInitiallyLoaded) {
      return of();
    }

    ctx.patchState({ customerAccountDataLoading: true });

    return this._customerInfrastructure.getCustomerAccount().pipe(
      tap((customerAccountData) =>
        ctx.patchState({ customerAccountData, customerAccountDataInitiallyLoaded: true, customerAccountDataLoading: false }),
      ),
      catchError((error: HttpErrorResponse) => {
        ctx.patchState({ customerAccountDataLoading: false });
        return this.handleBackendError(error);
      }),
    );
  }
}
